import React, { useState } from "react";
import { Download, FileText, FileSpreadsheet, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";

const REPORTS = [["claims", "Claims detail"], ["standings", "Team standings"]];

function ReportPicker({ value, onChange }) {
  return (
    <div className="grid grid-cols-2 gap-1 rounded-full bg-slate-100 p-1" role="radiogroup" data-testid="thespian-export-report">
      {REPORTS.map(([k, label]) => (
        <button key={k} type="button" role="radio" aria-checked={value === k} onClick={() => onChange(k)} data-testid={`thespian-export-report-${k}`}
          className={`rounded-full px-3 py-1.5 text-sm font-semibold transition-colors ${value === k ? "bg-white text-amber-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
          {label}
        </button>
      ))}
    </div>
  );
}

// PDF/CSV of approved claims, or (director only) the whole troupe's ITS standings. `roster` enables the director options.
export default function ThespianExportDialog({ endpoint, roster, own }) {
  const [open, setOpen] = useState(false);
  const [report, setReport] = useState("claims");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [member, setMember] = useState("all");
  const [busy, setBusy] = useState(null);
  const standings = !!roster && report === "standings";

  const download = async (fmt) => {
    if (!standings && from && to && from > to) { toast.error("The start date must be before the end date"); return; }
    setBusy(fmt);
    try {
      const params = standings ? { fmt } : { fmt, ...(from && { from_date: from }), ...(to && { to_date: to }), ...(member !== "all" && { member_id: member }) };
      const res = await api.get(standings ? "/thespian/standings/export" : endpoint, { params, responseType: "blob" });
      const name = (res.headers["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || `thespian-points.${fmt}`;
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data); a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch { toast.error("Unable to export Thespian points"); }
    finally { setBusy(null); }
  };

  return (
    <>
      <Button variant="outline" className="rounded-full" onClick={() => setOpen(true)} data-testid="thespian-export-btn"><Download className="mr-2 h-4 w-4" /> Export</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="thespian-export-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{own ? "Download my Thespian record" : "Export Thespian points"}</DialogTitle>
            <DialogDescription data-testid="thespian-export-description">
              {standings
                ? "Every active troupe member ranked by approved points, with ITS rank and points to the next rank — one page to print or post."
                : "Approved claims with documentation notes, cumulative points and ITS rank — ready for induction paperwork."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {roster && <ReportPicker value={report} onChange={setReport} />}
            {!standings && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Activity from</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="thespian-export-from" /></div>
                  <div className="space-y-1.5"><Label>Activity to</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="thespian-export-to" /></div>
                </div>
                {roster && (
                  <div className="space-y-1.5">
                    <Label>Team member</Label>
                    <Select value={member} onValueChange={setMember}>
                      <SelectTrigger data-testid="thespian-export-member"><SelectValue /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        <SelectItem value="all">Everyone</SelectItem>
                        {roster.map((m) => <SelectItem key={m.member_id} value={m.member_id} data-testid={`thespian-export-member-${m.member_id}`}>{m.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </>
            )}
            <p className="text-xs text-slate-500">{standings ? "Highest points first; pending claims are not counted." : "Leave dates empty to include every approved claim."}</p>
            <div className="grid grid-cols-2 gap-3">
              <Button className="rounded-full bg-amber-700 hover:bg-amber-800" disabled={!!busy} onClick={() => download("pdf")} data-testid="thespian-export-pdf">
                {busy === "pdf" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />} PDF
              </Button>
              <Button variant="outline" className="rounded-full" disabled={!!busy} onClick={() => download("csv")} data-testid="thespian-export-csv">
                {busy === "csv" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-2 h-4 w-4" />} CSV
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
